/**
 * Video.js control-bar indicator showing client sync status.
 *
 * Registered by WS-6's registerComponents.ts as the component named
 * 'SyncStatusIndicator'. It is a non-interactive Button that reflects the
 * custom `syncstatus` player event emitted by WS-8's sync engine
 * (`player.trigger('syncstatus', payload)`).
 *
 * This module owns the event name and payload contract:
 *  - {@link SYNC_STATUS_EVENT} — the player event name to listen for / trigger.
 *  - {@link SyncStatusPayload} — the event payload shape.
 *
 * @packageDocumentation
 */
import videojs from 'video.js';
import type Button from 'video.js/dist/types/button';
import type { Player } from '../useVideoJs';

/** The custom player event name used to communicate sync status (WS-7 ↔ WS-8). */
export const SYNC_STATUS_EVENT = 'syncstatus' as const;

/** Payload emitted by WS-8 via `player.trigger(SYNC_STATUS_EVENT, payload)`. */
export interface SyncStatusPayload {
  /** Coarse sync state computed by the client sync engine. */
  status: 'in-sync' | 'adjusting' | 'reconnecting';
  /** Current drift in seconds, when relevant (e.g. while adjusting). */
  driftSec?: number;
}

/** Maps a status to the `data-state` value consumed by WS-6's player.css. */
function statusToDataState(status: SyncStatusPayload['status']): string {
  // WHY: player.css colours use `data-state="synced"` for the in-sync state,
  // so we translate the payload's 'in-sync' to the CSS token 'synced'.
  if (status === 'in-sync') return 'synced';
  return status;
}

/** Human-readable label for each status, optionally annotated with drift. */
function statusLabel(payload: SyncStatusPayload): string {
  switch (payload.status) {
    case 'in-sync':
      return 'In sync';
    case 'adjusting':
      if (typeof payload.driftSec === 'number' && Number.isFinite(payload.driftSec)) {
        return `Adjusting · ${payload.driftSec.toFixed(1)} s`;
      }
      return 'Adjusting';
    case 'reconnecting':
      return 'Reconnecting…';
  }
}

// WHY: video.js's own typings declare getComponent() as returning
// `typeof Component`; narrow to the Button constructor type we actually
// extend (unknown as intermediate step since the two constructor shapes
// aren't statically related).
const VjsButton = videojs.getComponent('Button') as unknown as typeof Button;

/**
 * Custom Video.js Button (non-interactive) that displays sync status.
 *
 * Registered name: `SyncStatusIndicator` (matches the controlBar children
 * arrays in WS-6's controlBarConfig.ts).
 */
export class SyncStatusIndicator extends VjsButton {
  /** The element that holds the visible status text. */
  private labelEl: HTMLSpanElement | null = null;

  constructor(player: Player, options?: Record<string, unknown>) {
    super(player, options);

    this.controlText('Sync status');

    const label = videojs.dom.createEl('span', {
      className: 'vjs-sync-status-indicator-label',
    }) as HTMLSpanElement;
    this.el().appendChild(label);
    this.labelEl = label;

    // WHY: this is a status display, not a control. Remove it from the tab
    // order and neutralise the click/keyboard handling inherited from Button.
    this.el().setAttribute('tabindex', '-1');
    this.el().setAttribute('role', 'status');

    // Default to "In sync" until WS-8 emits a real status.
    this.update({ status: 'in-sync' });

    this.player().on(SYNC_STATUS_EVENT, this.onSyncStatus);
  }

  /** CSS class hook consumed by WS-6's player.css. */
  override buildCSSClass(): string {
    return `vjs-sync-status-indicator ${super.buildCSSClass()}`;
  }

  /** No-op: the indicator is non-interactive. */
  override handleClick(): void {
    // Intentionally empty — status display only.
  }

  /** Update the displayed status and colour state from a sync payload. */
  update(payload: SyncStatusPayload): void {
    if (this.labelEl) this.labelEl.textContent = statusLabel(payload);
    this.el().setAttribute('data-state', statusToDataState(payload.status));
  }

  /** Event handler bound to the player's `syncstatus` event. */
  private readonly onSyncStatus = (_event: unknown, payload: SyncStatusPayload): void => {
    if (payload && typeof payload === 'object' && typeof payload.status === 'string') {
      this.update(payload);
    }
  };

  /** Remove the event listener when the player is disposed. */
  override dispose(): void {
    this.player().off(SYNC_STATUS_EVENT, this.onSyncStatus);
    this.labelEl = null;
    super.dispose();
  }
}
